import {
  FC,
  useContext,
  useEffect,
  useState,
  useMemo,
  ChangeEvent,
  useRef,
} from "react";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import { useModalAction } from "../../components/modal-views/context";
import { useMutation } from "react-query";
import client from "../../api";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  currentTimeForDataPicker,
  getErrorMessage,
  getStatusStyle,
  logger,
} from "../../lib/utils";
import DropdownType from "../../components/ui/dropdownType";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { Dropdown, MenuProps, Space, Spin, Table } from "antd";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  userManagementFilterOptions_1,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { PaginationInput, User } from "src/api/types";
import SearchUserIDBox from "src/components/shared/search-box";
import {
  TransactionDataType,
  UserDataType,
} from "src/lib/constants/table-type";
import { ColumnsType } from "antd/es/table";
import { LockIcon } from "src/components/icons/lock-icon";
import { UnlockIcon } from "src/components/icons/unlock-icon";
import Button from "src/components/ui/button";
import ToggleSwitch from "src/components/ui/switch";
import { BackCircleIcon } from "src/components/icons/back-circle-icon";
import UsersContainerPage from "src/components/users";
import InvoicePage from "src/components/invoice";
import SearchFilter from "src/components/search-filters/user-management-filter";
import SearchFilter_1 from "src/components/search-filters/user-management-filter_1";
import {
  userManagementFilterOptions,
  // userManagementFilterOptions_1,
} from "src/lib/constants";
import { SpinnerIcon } from "src/components/icons/spinner-icon";
import moment from "moment";
import { DateRangePicker } from "rsuite";
import { useDateTimePicker } from "src/lib/hooks/useDateTimePicker";
import classNames from "classnames";

interface ILevelItem {
  key: number;
  code: string;
  name: string;
}

const level: ILevelItem[] = [
  {
    key: 0,
    code: "low",
    name: "Low",
  },
  { key: 1, code: "medium", name: "Medium" },
  { key: 2, code: "high", name: "High" },
];

interface IUserManagementPage {
  isBuisiness?: boolean;
}

interface ExportButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const ExportButton: FC<ExportButtonProps> = ({
  label,
  icon,
  onClick,
  disabled,
  loading,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={classNames(
      "flex items-center gap-2 h-[42px] px-4 rounded-[8px] text-[14px] font-medium border transition-colors",
      disabled || loading
        ? "opacity-50 cursor-not-allowed bg-white border-[#E6E8EC] text-secondary"
        : "bg-white border-[#E6E8EC] text-primary hover:border-brand hover:text-brand shadow-sm",
    )}
  >
    {loading ? (
      <svg
        className="animate-spin w-[18px] h-[18px] text-secondary"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
    ) : (
      icon
    )}
    {label}
  </button>
);

const UserManagementPage: FC<IUserManagementPage> = ({ isBuisiness }) => {
  const targetModule = isBuisiness
    ? permissionConstants.USER_MANAGEMENT_BUSINESS_USERS
    : permissionConstants.USER_MANAGEMENT_INDIVIDUAL_USERS;
  const {
    canView,
    canList,
    canExport,
    canDownload,
    canEdit,
    canBlock,
  } = usePermissions(targetModule);
  const canViewUsers = canView || canList;

  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const { id } = params;
  const curentId: number = id ? Number(id) : -1;

  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("");
  const [filter, setFilter] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedUserID, setSelectedUserID] = useState(Number(curentId));
  const [userData, setUserData] = useState<User>();
  const [blockUserID, setBlockUserID] = useState(-1);
  const [levelUserID, setLevelUserID] = useState(-1);

  const pathCurrent = location.pathname.split("/")[1];
  const [dataSource, setDataSource] = useState<UserDataType[]>([]);
  const [total, setTotal] = useState(0);
  const [totalUsers, setTotalUsers] = useState<User[]>([]);
  const { setIsAlertOpened, setAlertText, setAlertTitle } =
    useContext(GlobalContext);
  const isMounted = useIsMounted();

  const [invoiceData, setInvoiceData] = useState<TransactionDataType | null>(
    null,
  );
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportUserId, setExportUserId] = useState<number>();
  const [exportDateRange, setExportDateRange] = useState<[Date, Date] | null>([
    new Date(),
    new Date(),
  ]);

  const onDateSelect = (_startDate: string | null, _endDate: string | null) => {
    setStartDate(_startDate);
    setEndDate(_endDate);
  };

  const { afterToday } = DateRangePicker;
  const { dateRangeValue, changeDateRangeHandler } = useDateTimePicker({
    onDateSelect,
  });

  const now = new Date();
  const prevMonth = new Date(now.setMonth(now.getMonth() - 1));

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    containerRef.current && setContainerWidth(containerRef.current.offsetWidth);
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onSelectedInvoiceData = (data: TransactionDataType) => {
    setInvoiceData(data);
  };

  const onBackFromInvoice = () => {
    setInvoiceData(null);
  };

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const { mutate: getUsers, isLoading } = useMutation(
    isBuisiness
      ? client.users.getBuisinessUsers
      : client.users.getIndividualUsers,
    {
      onSuccess: (data) => {
        setTotal(data.total);
        setTotalUsers(data.users);
        setDataSource(
          data.users.map((item) => ({
            key: item.id,
            id: item.id,
            bitanica_id: item?.bitanica_user_id,
            full_name:
              !item.user_data.first_name && !item.user_data.last_name
                ? "__"
                : (item.user_data.first_name ?? " ") +
                " " +
                (item.user_data.last_name ?? " "),
            level: item.is_vip === 1 ? "VIP" : "Regular",
            email: item.email,
            phone: item.user_data.phone ?? "__",
            country: item?.country
              ? item?.country
              : (item?.source_country ?? "__"),

            sourceIp: item?.source_ip ?? "__",
            sourceType: item?.source_type ?? "__",
            login_type: item?.login_type ?? "__",
            register_type:
              item?.register_type != null
                ? item?.register_type
                : item?.register_type == null && item?.bitanica_user_id != null
                  ? "Fastoo"
                  : "__",
            bitanica_connected_at: item?.bitanica_connected_at ?? "__",
            createdAt: item?.created_at ?? "__",
            verifiedAt: item?.verification_completed_at ?? "__",
            phoneVerifiedAt: item?.phone_verified_at ?? "__",

            lastLogin:
              item?.latest_login_histories.length > 0
                ? item?.latest_login_histories[0].logged_at
                : "__",
            blocked: item.blocked,
            is_business: item.is_business ?? "__",
            status: item.deleted_at !== null ? "deactivated" : item.status.name,
            is_vip: item.is_vip,
            risk_level: item.risk_level,
            payment_reference: item.payment_reference ?? "__",
            ...(isBuisiness
              ? { taxCode: item?.aml_business_data?.tax_code ?? "__" }
              : {
                tin: item?.aml_user_data?.tin
                  ? item?.aml_user_data?.tin
                  : item?.user_data?.personal_code
                    ? item?.user_data?.personal_code
                    : "__",
              }),
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: exportUsers, isLoading: exportDashBoardLoading } =
    useMutation(
      isBuisiness
        ? client.users.getBuisinessUsers
        : client.users.getIndividualUsers,
      {
        onSuccess: (data) => {
          if (data?.download_url) {
            // Create an invisible link and trigger download
            const link = document.createElement("a");
            link.href = data.download_url;
            link.download = "transactions.xlsx"; // optional: customize filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          setAlertText("Export success");
          setIsAlertOpened(true);
        },
        onError: (error: any) => {
          if (error.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else {
            if (error.response) {
              setAlertText(getErrorMessage(error.response.data));
            } else {
              setAlertText(APISomethingWrongMsg);
            }
          }
          setIsAlertOpened(true);
        },
      },
    );

  const { mutate: exportExcel, isLoading: isExcelLoading } = useMutation(
    client.newTransactionsHistory.ExportAllPaymentsHistoryExcel,
    {
      onSuccess: (data: any) => {
        setAlertTitle("Success");
        setAlertText(
          data?.message,
          // String(
          //   L.translate("Success.ExportStarted") ??
          //     "Excel export started successfully.",
          // ),
        );
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  // ── Export PDF Mutation ──
  const { mutate: exportPdf, isLoading: isPdfLoading } = useMutation(
    client.newTransactionsHistory.ExportAllPaymentsHistoryPdf,
    {
      onSuccess: (data: any) => {
        setAlertTitle("Success");
        setAlertText(
          data?.message,
          // String(
          //   L.translate("Success.ExportStarted") ??
          //     "PDF export started successfully.",
          // ),
        );
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const searchFields = [
    "user_id",
    "full_name",
    "phone",
    "email",
    "personal_number",
    "payment_reference",
  ];

  const getFilterParams = () => {
    const isSearchField = searchFields.includes(filter);

    if (isSearchField) {
      // Do not send search_field until the user enters a search value
      if (!searchText.trim()) {
        return {
          search_field: undefined,
          search_value: undefined,
          status_field: undefined,
          status_value: undefined,
        };
      }

      return {
        search_field: filter,
        search_value: searchText.trim(),
        status_field: undefined,
        status_value: undefined,
      };
    }

    return {
      search_field: undefined,
      search_value: searchText || undefined,
      status_field: filter || undefined,
      status_value: filterValue || undefined,
    };
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      // search_value?: number | string;
      // status_value?: string;
      // status_field?: string;
      if (isMounted && canViewUsers) {
        getUsers({
          ...searchParams,
          ...getFilterParams(),
          start_date: startDate,
          end_date: endDate,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isMounted,
    canViewUsers,
    searchParams.current_page,
    searchText,
    isBuisiness,
    filterValue,
    startDate,
    endDate,
    filter,
  ]);

  const exportBtn = async () => {
    if (!canExport && !canDownload) return;
    try {
      setTimeout(() => {
        exportUsers({
          ...searchParams,
          ...getFilterParams(),
          start_date: startDate,
          end_date: endDate,
          export: 1,
        });
      }, 300);
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };

  useEffect(() => {
    if (!id) {
      setSelectedUserID(-1);
    }
  }, [id]);

  useEffect(() => {
    if (id && canViewUsers) {
      getUserData(Number(selectedUserID));
    }
  }, [id, canViewUsers]);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      current_page: 1,
    });
  }, [searchText]);

  const { mutate: blockUser, isLoading: isLoadingBlock } = useMutation(
    client.users.blockUser,
    {
      onSuccess: (data) => {
        setAlertText(
          data.blocked === 1
            ? "Account has been blocked"
            : "Account has been unblocked",
        );
        setIsAlertOpened(true);

        const newData = [...dataSource];
        const index = newData.findIndex((item) => blockUserID == item.key);
        if (index > -1) {
          const item = newData[index];
          const editingData = { ...item, blocked: data.blocked };
          newData.splice(index, 1, {
            ...item,
            ...editingData,
          });
          setDataSource(newData);
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: updateRiskLevel, isLoading: isLoadingRisk } = useMutation(
    isBuisiness
      ? client.users.riskLevelBusiness
      : client.users.riskLevelIndividual,

    {
      onSuccess: (data) => {
        const newData = [...dataSource];
        const index = newData.findIndex((item) => levelUserID == item.key);
        if (index > -1) {
          const item = newData[index];
          const editingData = { ...item, risk_level: data.risk_level };
          newData.splice(index, 1, {
            ...item,
            ...editingData,
          });
          setDataSource(newData);
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: updateVip, isLoading: isLoadingVip } = useMutation(
    isBuisiness
      ? client.users.updateBuisinessVip
      : client.users.updateIndividualVip,
    {
      onSuccess: (data) => {
        const newData = [...dataSource];
        const index = newData.findIndex((item) => data.id == item.key);
        if (index > -1) {
          const item = newData[index];
          const editingData = { ...item, is_vip: data.is_vip };
          newData.splice(index, 1, {
            ...item,
            ...editingData,
          });
          setDataSource(newData);
        }
        getUsers({
          ...searchParams,
          ...getFilterParams(),
          start_date: startDate,
          end_date: endDate,
        });
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: getUserData } = useMutation(
    isBuisiness
      ? client.busienessUserSettings.all
      : client.individualUserSettings.all,
    {
      onSuccess: (data: any) => {
        setUserData(data);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
      },
    },
  );

  const onBlockUser = (
    id: number,
    currentStatus: number,
    newStatus: number,
    type: number,
  ) => {
    if (!canBlock) {
      return;
    }
    if (currentStatus === newStatus) {
      return;
    }

    setBlockUserID(id);
    setTimeout(
      () =>
        blockUser({
          id,
          blocked: newStatus,
          type: type ? "business" : "individual",
        }),
      100,
    );
  };

  const updateRisk = (risk: string, id: number) => {
    if (!canEdit) {
      return;
    }
    setLevelUserID(id);
    setTimeout(
      () =>
        updateRiskLevel({
          id,
          risk_level: risk,
        }),
      100,
    );
  };

  const onSelectUser = (userID: number) => {
    if (!canView) {
      return;
    }
    setSelectedUserID(userID);
    navigate(`/${pathCurrent}/${String(userID)}`, { replace: true });
  };

  const onUpdateVip = (id: number, value: boolean) => {
    if (!canEdit) {
      return;
    }
    updateVip({
      userId: id,
      input: {
        is_vip: value ? true : false,
      },
    });
  };

  const buildExportPayload = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
      user_id: exportUserId,
      from: exportDateRange
        ? moment(exportDateRange[0]).format("YYYY-MM-DD")
        : undefined,
      to: exportDateRange
        ? moment(exportDateRange[1]).format("YYYY-MM-DD")
        : undefined,
      user_type: isBuisiness ? "business" : "individual",
      timezone,
    };
  };

  const handleExportExcel = () => {
    if (!canDownload) return;
    exportExcel({
      filters: buildExportPayload(),
    });
  };

  const handleExportPdf = () => {
    if (!canDownload) return;
    exportPdf({
      filters: buildExportPayload(),
    });
  };

  const openExportModal = (record: UserDataType) => {
    if (!canDownload) return;
    setExportUserId(record.id);
    setExportDateRange([new Date(), new Date()]);
    setExportModalOpen(true);
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <a className="text-primary text-[13px] font-manrope font-light">Lock</a>
      ),
    },
    {
      key: "2",
      label: (
        <a className="text-primary text-[13px] font-manrope font-light">
          Unlock
        </a>
      ),
    },
  ];

  const columns: ColumnsType<UserDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      className: "w-auto",
      key: "id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          {canView ? (
            <a
              className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline cursor-pointer"
              onClick={() => onSelectUser(value)}
            >
              {value}
            </a>
          ) : (
            <span className="font-manrope text-secondary text-[14px] font-medium">
              {value}
            </span>
          )}
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Bitanica ID
    //     </p>
    //   ),
    //   dataIndex: "bitanica_id",
    //   className: "w-auto",
    //   key: "bitanica_id",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       {/* <a
    //         className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline"
    //         onClick={() => onSelectUser(value)}
    //       > */}
    //       {value || "__"}
    //       {/* </a> */}
    //     </div>
    //   ),
    // },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Reference
    //     </p>
    //   ),
    //   dataIndex: "payment_reference",
    //   className: "w-auto",
    //   key: "payment_reference",
    //   render: (value: number) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       <p className="font-manrope text-secondary text-[14px] font-medium">
    //         {value}
    //       </p>
    //     </div>
    //   ),
    // },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Bitanica ID
        </p>
      ),
      dataIndex: "bitanica_id",
      className: "w-auto",
      key: "bitanica_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          {/* <a
            className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline"
            onClick={() => onSelectUser(value)}
          > */}
          {value || "__"}
          {/* </a> */}
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Reference
        </p>
      ),
      dataIndex: "payment_reference",
      className: "w-auto",
      key: "payment_reference",
      render: (value: number) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Full name
        </p>
      ),
      dataIndex: "full_name",
      className: "w-auto",
      key: "full_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {capitalizeEachFirstLetter(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          E-mail
        </p>
      ),
      dataIndex: "email",
      className: "w-auto",
      key: "email",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {isBuisiness ? "Tax Code" : "ID Number"}
        </p>
      ),
      dataIndex: isBuisiness ? "taxCode" : "tin",
      className: "w-auto",
      key: isBuisiness ? "taxCode" : "tin",
      render: (value: number) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Phone number
        </p>
      ),
      dataIndex: "phone",
      key: "phone",
      className: "w-auto",
      render: (value: number) => (
        <p className="font-manrope text-primary text-[14px] font-normal">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Country
        </p>
      ),
      dataIndex: "country",
      className: "w-auto",
      key: "country",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          IP Address
        </p>
      ),
      dataIndex: "sourceIp",
      className: "w-auto",
      key: "sourceIp",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Device
        </p>
      ),
      dataIndex: "sourceType",
      className: "w-auto",
      key: "sourceType",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {capitalizeFirstLetter(value || "")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Connection Source
        </p>
      ),
      dataIndex: "register_type",
      className: "w-auto",
      key: "register_type",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {capitalizeFirstLetter(value || "")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Connection Date
        </p>
      ),
      dataIndex: "bitanica_connected_at",
      className: "w-auto",
      key: "bitanica_connected_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {moment(value).isValid()
              ? moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")
              : value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Signup Method
        </p>
      ),
      dataIndex: "login_type",
      className: "w-auto",
      key: "login_type",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {capitalizeFirstLetter(value || "")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Registration Date
        </p>
      ),
      dataIndex: "createdAt",
      className: "w-auto",
      key: "country",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {value ? moment.unix(value).format("YYYY-MM-DD HH:mm:ss") : "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Verification Date
        </p>
      ),
      dataIndex: "verifiedAt",
      className: "w-auto",
      key: "verifiedAt",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {moment(value).isValid()
              ? moment(value).format("YYYY-MM-DD HH:mm:ss")
              : value}
          </p>
        </div>
      ),
    },
    //phoneVerifiedAt
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Phone Verification Date
        </p>
      ),
      dataIndex: "phoneVerifiedAt",
      className: "w-auto",
      key: "phoneVerifiedAt",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {value !== "-"
              ? moment.unix(value).format("YYYY-MM-DD HH:mm:ss")
              : "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Last Login
        </p>
      ),
      dataIndex: "lastLogin",
      className: "w-auto",
      key: "lastLogin",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {value != "__"
              ? moment.unix(value).format("YYYY-MM-DD HH:mm:ss")
              : "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      key: "status",
      className: "w-auto",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center rounded-full">
          <p
            className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getStatusStyle(
              value,
            )}`}
          >
            {capitalizeFirstLetter(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          blocked
        </p>
      ),
      dataIndex: "blocked",
      key: "blocked",
      className: "w-auto",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center rounded-full">
          <p
            className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getStatusStyle(
              value,
            )}`}
          >
            {value != 1 ? "False" : "True"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Risk Level
        </p>
      ),
      className: "w-auto",
      dataIndex: "risk_level",
      // key: "risk_level",
      render: (
        _,
        record: { key: React.Key; risk_level?: string; id: number },
      ) => {
        const handleSelect = (value: ILevelItem): void => {
          updateRisk(value.code, record.id);
        };
        return (
          <div
            className="flex flex-row gap-2 items-center rounded-full justify-between"
            style={{ width: "90%" }}
          >
            <p className="font-manrope text-[14px] font-medium px-2 rounded-full">
              {capitalizeFirstLetter(String(record.risk_level))}
            </p>
            {canEdit && (
              <DropdownType
                label=""
                data={level}
                value={String(record?.risk_level)}
                onChange={handleSelect}
              />
            )}
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Level
        </p>
      ),
      className: "w-auto",
      render: (value) => {
        return (
          <div
            className="flex flex-row gap-2 items-center rounded-full justify-between"
            style={{ width: "90%" }}
          >
            <p className="font-manrope text-[14px] font-medium px-2 rounded-full">
              {value.is_vip ? "VIP" : "Regular"}
            </p>
            {canEdit ? (
              <ToggleSwitch
                type="isVip"
                value={value.is_vip === 1}
                onValueChanged={() => onUpdateVip(value.id, !value.is_vip)}
                disabled={isLoadingVip}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (
        _,
        record: { key: React.Key; blocked: number; is_business: number },
      ) => {
        const showBlock = canBlock;
        const showExport = canDownload;

        if (!showBlock && !showExport) {
          return <span className="text-secondary text-[13px]">—</span>;
        }

        return (
          <div className="flex flex-row gap-3 items-center">
            {showBlock && (
              <>
                <button
                  className="w-[30px] h-[30px] flex items-center justify-center"
                  onClick={() =>
                    onBlockUser(
                      record.key as number,
                      record.blocked,
                      1,
                      record.is_business,
                    )
                  }
                  title="Lock account"
                >
                  <LockIcon
                    className={`${record.blocked == 1 ? "text-primary" : "text-secondary"
                      } w-[20px] h-[20px]`}
                  />
                </button>
                <button
                  className="w-[30px] h-[30px] flex items-center justify-center"
                  onClick={() =>
                    onBlockUser(
                      record.key as number,
                      record.blocked,
                      0,
                      record.is_business,
                    )
                  }
                  title="Unlock account"
                >
                  <UnlockIcon
                    className={`${record.blocked == 0 ? "text-primary" : "text-secondary"
                      } w-[20px] h-[20px]`}
                  />
                </button>
              </>
            )}
            {showExport && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => openExportModal(record as UserDataType)}
                title="Export End of Day Balance"
              >
                <ExportIcon className="w-[18px] h-[18px] text-primary hover:text-brand" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const mobileColumns: ColumnsType<UserDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData =
          dataSource.filter((item) => item.key === record.key)[0] || {};

        const onClick: MenuProps["onClick"] = ({ key }) => {
          if (key === "1") {
            onBlockUser(
              record.key as number,
              selData.blocked,
              1,
              selData.is_business,
            );
          } else {
            onBlockUser(
              record.key as number,
              selData.blocked,
              0,
              selData.is_business,
            );
          }
        };

        return (
          <div className="flex flex-row items-start justify-between gap-2">
            <div
              className="flex flex-col items-start gap-2"
              style={{ width: (containerWidth - 20) / 2 }}
            >
              <div className="w-full flex flex-row items-center justify-between gap-2 leading-[21px] min-h-[21px]">
                <p className="text-secondary text-[14px] font-manrope font-medium flex-shrink">
                  ID
                </p>
                {canView ? (
                  <a
                    className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline text-right cursor-pointer"
                    onClick={() => onSelectUser(selData.id)}
                  >
                    {selData.id}
                  </a>
                ) : (
                  <span className="font-manrope text-secondary text-[14px] font-medium text-right">
                    {selData.id}
                  </span>
                )}
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2 leading-[21px] min-h-[21px]">
                <p className="text-secondary text-[14px] font-manrope font-medium flex-shrink">
                  Reference
                </p>
                {canView ? (
                  <a
                    className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline text-right cursor-pointer"
                    onClick={() => onSelectUser(selData.id)}
                  >
                    {selData.payment_reference}
                  </a>
                ) : (
                  <span className="font-manrope text-secondary text-[14px] font-medium text-right">
                    {selData.payment_reference}
                  </span>
                )}
              </div>
              {/* <div className="w-full flex flex-row items-center justify-between gap-2 leading-[21px] min-h-[21px]">
                <p className="text-secondary text-[14px] font-manrope font-medium flex-shrink">
                  Reference
                </p>
                <a
                  className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline  text-right"
                  onClick={() => onSelectUser(selData.id)}
                >
                  {selData.payment_reference}
                </a>
              </div> */}
              <div className="w-full flex flex-row items-center justify-between gap-2 leading-[21px] min-h-[21px]">
                <p className="text-secondary text-[14px] font-manrope font-medium flex-shrink">
                  Full Name
                </p>
                <p className="font-manrope text-primary text-[14px] font-medium  text-right">
                  {capitalizeEachFirstLetter(selData.full_name)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2 leading-[21px] min-h-[21px]">
                <p className="text-secondary text-[14px] font-manrope font-medium flex-shrink">
                  Risk Level
                </p>
                <p className="font-manrope text-primary text-[14px] font-medium  text-right">
                  {capitalizeEachFirstLetter(String(selData.risk_level))}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2 leading-[21px] min-h-[21px]">
                <p className="text-secondary text-[14px] font-manrope font-medium flex-shrink">
                  Level
                </p>
                <div className="font-manrope text-primary text-[14px] font-medium text-right">
                  <div
                    className="flex flex-row gap-2 items-center rounded-full justify-between"
                    style={{ width: "90%" }}
                  >
                    <p className="font-manrope text-[14px] font-medium px-2 rounded-full">
                      {selData.is_vip ? "VIP" : "Regular"}
                    </p>
                    {canEdit && (
                      <ToggleSwitch
                        type="isVip"
                        value={selData.is_vip === 1}
                        onValueChanged={() =>
                          onUpdateVip(selData.id, !selData.is_vip)
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2 leading-[21px] min-h-[21px]">
                <p className="text-secondary text-[14px] font-manrope font-medium flex-shrink">
                  Status
                </p>
                <p
                  className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getStatusStyle(
                    selData.status,
                  )}`}
                >
                  {capitalizeFirstLetter(selData.status)}
                </p>
              </div>
            </div>
            <div
              className="flex flex-col items-start gap-2"
              style={{ width: (containerWidth - 20) / 2.4 }}
            >
              <p className="break-words inline-block w-full font-manrope text-secondary text-[14px] font-medium leading-[21px] min-h-[21px] text-right">
                {selData.email}
              </p>
              <p className="w-full truncate font-manrope text-primary text-[14px] font-normal leading-[21px] min-h-[21px] text-right">
                {selData.phone}
              </p>
              <p className="w-full truncate font-manrope text-primary text-[14px] font-normal leading-[21px] min-h-[21px] text-right">
                {selData.country}
              </p>
              {canBlock && (
                <Space direction="vertical" className="w-full text-right">
                  <Dropdown menu={{ items, onClick }}>
                    <a className="w-full truncate font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline leading-[21px] min-h-[21px] text-right cursor-pointer">
                      Actions
                    </a>
                  </Dropdown>
                </Space>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  /*
  const makeSubPage = () => {
    return invoiceData ? (
      <InvoicePage data={invoiceData!} callback={onBackFromInvoice} />
    ) : (
      <div className="flex flex-col w-full p-2 sm:p-4">
        <div className="flex flex-row items-center justify-between sm:justify-start gap-4">
          <div className="flex flex-row items-center gap-2">
            <Button
              variant="icon"
              className="w-[24px] bg-transparent h-[24px]"
              onClick={() => setSelectedUserID(-1)}
            >
              <BackCircleIcon className="w-[24px] h-[24px] text-primary" />
            </Button>
            <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[38px] sm:leading-[48px]">
              {
                dataSource.filter((item) => item.key === selectedUserID)[0]
                  .full_name
              }
            </p>
          </div>
          <p
            className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getStatusStyle(
              dataSource.filter((item) => item.key === selectedUserID)[0].status
            )}`}
          >
            {capitalizeFirstLetter(
              dataSource.filter((item) => item.key === selectedUserID)[0].status
            )}
          </p>
        </div>
        <UsersContainerPage
          user={totalUsers.filter((item) => item.id === selectedUserID)[0]}
          onSelectedInvoiceData={onSelectedInvoiceData}
        />
      </div>
    );
  };
  */

  const selectedUser = totalUsers.find(
    (item) => item.id === Number(selectedUserID),
  );

  const handleStates = () => {
    setSearchText("");
    setSearchField("");
    setFilter("");
    setFilterValue("");
    setStartDate(null);
    setEndDate(null);
    changeDateRangeHandler(null);
  };
  useEffect(() => {
    handleStates();
  }, [isBuisiness]);

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col">
        {!canViewUsers ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] m-2 sm:m-4 p-8 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view {isBuisiness ? "business" : "individual"} user management details.
            </p>
          </div>
        ) : selectedUserID < 0 ? (
          <div className="flex flex-col w-full p-2 sm:p-4">
            <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
              User Management
            </p>
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <p className="text-[16px] font-manrope font-medium leading-[24px] text-primary">
                {`All Users (${total})`}
              </p>
              <div className="flex align-center gap-2">
                {/* <div className="">
                  <SearchFilter_1
                    options={userManagementFilterOptions_1}
                    placeholder="Search by UserId,Full Name,E-mail,Country,Phone Number"
                    value={filter}
                    onChange={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                  />
                </div> */}
                <DateRangePicker
                  format="dd/MM/yyyy"
                  size="lg"
                  block
                  placement="bottomEnd"
                  style={{ width: "308px" }}
                  value={dateRangeValue}
                  disabledDate={afterToday?.()}
                  onChange={(range) => {
                    setSearchParams({ ...searchParams, current_page: 1 });
                    if (range) {
                      const currentTime: Date[] =
                        currentTimeForDataPicker(range);
                      changeDateRangeHandler([currentTime[0], currentTime[1]]);
                    } else {
                      changeDateRangeHandler(null);
                    }
                  }}
                  defaultCalendarValue={[prevMonth, now]}
                  ranges={[]}
                />

                <SearchFilter
                  options={userManagementFilterOptions}
                  value={filter}
                  onChange={setFilter}
                  filterValue={filterValue}
                  setFilterValue={setFilterValue}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  setSearchField={setSearchField}
                />
                <button
                  type="button"
                  onClick={handleStates}
                  className="button button--type3 button--gap30"
                >
                  Clear
                </button>
                <PermissionGate
                  module={targetModule}
                  action="can_export"
                >
                  <button
                    type="button"
                    onClick={() => exportBtn()}
                    className="button button--type3 button--gap30 min-w-[160px]"
                    style={{ minWidth: 160 }}
                    disabled={exportDashBoardLoading}
                  >
                    {exportDashBoardLoading ? (
                      <div className="flex justify-center items-center w-full">
                        <SpinnerIcon className="h-auto w-5 animate-spin" />
                      </div>
                    ) : (
                      <>
                        Export Excel
                        <span className="btn-icon btn-icon--fill">
                          <svg
                            width="17"
                            height="17"
                            viewBox="0 0 17 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                              stroke="#232800"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            ></path>
                          </svg>
                        </span>
                      </>
                    )}
                  </button>
                </PermissionGate>
              </div>
            </div>
            <div
              className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4"
              ref={containerRef}
            >
              <Table
                loading={isLoading || isLoadingBlock}
                showSorterTooltip={false}
                columns={isMobile ? mobileColumns : columns}
                dataSource={dataSource}
                pagination={{
                  total,
                  current: searchParams.current_page,
                  onChange: onPageChange,
                  position: ["bottomCenter"],
                  defaultPageSize: LIST_PAGE_SIZE,
                  showSizeChanger: false,
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <div
              className={`${invoiceData && "hidden"
                } flex flex-col w-full p-2 sm:p-4`}
            >
              <div className="flex flex-row items-center justify-between sm:justify-start gap-4">
                <div className="flex flex-row items-center gap-2">
                  <Button
                    variant="icon"
                    className="w-[24px] bg-transparent h-[24px]"
                    onClick={() => {
                      setSelectedUserID(-1);
                      navigate(`/${pathCurrent}`, { replace: true });
                      getUsers({
                        ...searchParams,
                        ...getFilterParams(),
                        start_date: startDate,
                        end_date: endDate,
                      });
                    }}
                  >
                    <BackCircleIcon className="w-[24px] h-[24px] text-primary" />
                  </Button>
                  <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[38px] sm:leading-[48px]">
                    {
                      dataSource.filter(
                        (item) => item.key === selectedUserID,
                      )?.[0]?.full_name
                    }
                  </p>
                </div>
                <p
                  className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getStatusStyle(
                    dataSource.filter(
                      (item) => item.key === selectedUserID,
                    )?.[0]?.status,
                  )}`}
                >
                  {capitalizeFirstLetter(
                    dataSource.filter(
                      (item) => item.key === selectedUserID,
                    )?.[0]?.status || "",
                  )}
                </p>
              </div>
              {userData && (
                <UsersContainerPage
                  user={userData}
                  onSelectedInvoiceData={onSelectedInvoiceData}
                  isBuisiness={isBuisiness}
                />
              )}
            </div>
            {invoiceData && (
              <InvoicePage data={invoiceData!} callback={onBackFromInvoice} />
            )}
          </div>
        )}
      </div>
      {exportModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-[420px] p-6">
            <h2 className="text-[20px] font-semibold mb-5">
              Export End of Day Balance
            </h2>

            <div className="mb-4">
              <label className="text-secondary text-[13px]">User ID</label>

              <div className="mt-1 p-3 rounded-md bg-[#F7F8FA] font-semibold">
                {exportUserId}
              </div>
            </div>

            <div className="mb-5">
              <label className="text-secondary text-[13px]">Date Range</label>

              <DateRangePicker
                format="dd/MM/yyyy"
                value={exportDateRange}
                onChange={(value) => {
                  if (value && value.length === 2) {
                    setExportDateRange([value[0], value[1]]);
                  } else {
                    setExportDateRange(null);
                  }
                }}
                disabledDate={DateRangePicker.afterToday()}
                ranges={[]}
                placement="bottomEnd"
                style={{ width: "100%" }}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="button button--type3"
                onClick={() => setExportModalOpen(false)}
              >
                Cancel
              </button>

              <ExportButton
                label="Excel"
                icon={<ExcelIcon />}
                onClick={() => {
                  handleExportExcel();
                  setExportModalOpen(false);
                }}
                loading={isExcelLoading}
              />

              <ExportButton
                label="PDF"
                icon={<PdfIcon />}
                onClick={() => {
                  handleExportPdf();
                  setExportModalOpen(false);
                }}
                loading={isPdfLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;

const ExcelIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PdfIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M9 15v-2h2a1 1 0 0 1 0 2H9z" />
    <path d="M9 18v-1" />
    <path d="M13 13v5" />
    <path d="M13 13h1.5a1.5 1.5 0 0 1 0 3H13" />
  </svg>
);

const ExportIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);
